import { Link } from "@/lib/link";
import { useTranslation } from "react-i18next";
import { GraduationCap, Mail, Linkedin, MapPin } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import SectionHeading from "@/components/common/SectionHeading";
import ScrollReveal from "@/components/common/ScrollReveal";
import { Button } from "@/components/ui/button";
import { founders, teamMembers } from "@/data/team";

export default function About() {
    const { t } = useTranslation();

    return (
        <>
            <SEO
                title="About Exportfy.world — Founder Story, Team & 5-Year Journey"
                description="Meet the founders of Exportfy.world — a 5+ year old global merchant exporter. Cofounders Asif Hakeem and Nitin Yadav, plus the sourcing, QC, and buyer-success team."
                path="/about"
            />
            <PageHero
                eyebrow={t("about.hero.eyebrow")}
                title={t("about.hero.title")}
                subtitle={t("about.hero.subtitle")}
                image="https://images.unsplash.com/photo-1527314392553-2c7bded21b23?w=1600"
            />

            {/* Company facts */}
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-14">
                    <ScrollReveal className="lg:col-span-1">
                        <div className="overline mb-4">{t("about.story.eyebrow")}</div>
                        <h2 className="text-3xl lg:text-4xl font-extrabold text-[#0A2540] tracking-tight leading-tight">
                            {t("about.story.title")}
                        </h2>
                    </ScrollReveal>
                    <ScrollReveal className="lg:col-span-2 space-y-6 text-slate-600 text-lg leading-relaxed">
                        <p>
                            {t("about.story.paragraph1")}
                        </p>
                        <p>
                            {t("about.story.paragraph2")}
                        </p>
                        <p>
                            {t("about.story.paragraph3")}
                        </p>
                    </ScrollReveal>
                </div>
            </section>

            {/* Founders */}
            <section className="section-y bg-[#F8FAFC]">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading eyebrow={t("about.founders.eyebrow")} title={t("about.founders.title")} />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {founders.map((f, i) => (
                            <ScrollReveal key={f.name} delay={i * 100}>
                                <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm h-full flex flex-col">
                                    <div className="overflow-hidden bg-slate-100 flex items-center justify-center p-3 md:p-4">
                                        <img src={f.image} alt={f.name} className="w-full h-full max-h-[320px] object-contain object-center rounded-xl" loading="lazy" />
                                    </div>
                                    <div className="p-7 md:p-8 flex-1">
                                        <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-bold">{f.role}</div>
                                        <h3 className="mt-2 text-2xl font-extrabold text-[#0A2540]">{f.name}</h3>
                                        <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-500">
                                            <GraduationCap className="w-4 h-4" /> {f.education}
                                        </div>
                                        <p className="mt-5 text-slate-600 leading-relaxed text-[15px]">{f.story}</p>
                                        <p className="mt-3 text-slate-500 leading-relaxed text-[14px]">{f.bio}</p>
                                        <div className="mt-6 flex items-center gap-3">
                                            <a href="#" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0A2540] flex items-center justify-center"><Linkedin className="w-4 h-4" /></a>
                                            <a href="mailto:hello@exportfy.world" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0A2540] flex items-center justify-center"><Mail className="w-4 h-4" /></a>
                                        </div>
                                    </div>
                                </article>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading eyebrow={t("about.team.eyebrow")} title={t("about.team.title")} subtitle={t("about.team.subtitle")} />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {teamMembers.map((m, i) => (
                            <ScrollReveal key={m.name} delay={i * 40}>
                                <div className="text-center">
                                    <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100">
                                        <img src={m.image} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                    <div className="mt-4 font-bold text-[#0A2540] text-sm">{m.name}</div>
                                    <div className="text-xs text-slate-500">{m.role}</div>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="section-y bg-[#0A2540] text-white">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading light eyebrow={t("about.values.eyebrow")} title={t("about.values.title")} />
                    </ScrollReveal>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { t: t("about.valuesItems.ownership.title"), d: t("about.valuesItems.ownership.description") },
                            { t: t("about.valuesItems.transparency.title"), d: t("about.valuesItems.transparency.description") },
                            { t: t("about.valuesItems.longGames.title"), d: t("about.valuesItems.longGames.description") },
                            { t: t("about.valuesItems.clusterDepth.title"), d: t("about.valuesItems.clusterDepth.description") },
                        ].map((v) => (
                            <div key={v.t} className="rounded-2xl bg-white/5 border border-white/10 p-7">
                                <div className="text-emerald-400 font-bold text-sm tracking-widest uppercase">{v.t}</div>
                                <p className="mt-4 text-white/80 leading-relaxed">{v.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-y">
                <div className="container-x flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <div className="overline mb-3">{t("about.cta.eyebrow")}</div>
                        <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]">{t("about.cta.title")}</h3>
                        <p className="mt-2 text-slate-500 max-w-xl">{t("about.cta.subtitle")}</p>
                    </div>
                    <Link to="/consultation"><Button className="rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] px-7 py-6">{t("about.cta.button")}</Button></Link>
                </div>
            </section>
        </>
    );
}
